import { useTranslation } from "react-i18next";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent, Button } from "@tellescope/ui";
import { ChevronLeft } from "lucide-react";
import type { useWeightMacroTrackerBlock } from "./useWeightMacroTrackerBlock";
import { useLogEntryView } from "./useLogEntryView";
import { WeightEntryField } from "./WeightEntryField";
import { WaterEntryField } from "./WaterEntryField";
import { FoodEntrySection } from "./FoodEntrySection";

type TrackerState = ReturnType<typeof useWeightMacroTrackerBlock>;

interface LogEntryViewProps {
  state: TrackerState;
  editEntryId: string | undefined;
}

export const LogEntryView = ({ state, editEntryId }: LogEntryViewProps) => {
  const { t } = useTranslation("portal");
  const { editingEntry, editingType, updateWeight, updateWater, updateFood } = useLogEntryView(state, editEntryId);

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={state.goBackFromLogEntry} className="-ml-2">
        <ChevronLeft className="h-4 w-4 mr-1" />
        {t("Back")}
      </Button>

      {editingEntry ? (
        <>
          {editingType === "weight" && (
            <WeightEntryField
              latestWeight={state.latestWeight}
              editingEntry={editingEntry}
              onLog={state.logWeight}
              onUpdate={updateWeight}
              isPending={state.isUpdating}
            />
          )}
          {editingType === "water" && (
            <WaterEntryField
              editingEntry={editingEntry}
              onLog={state.logWater}
              onUpdate={updateWater}
              isPending={state.isUpdating}
            />
          )}
          {editingType === "food" && (
            <FoodEntrySection
              editingEntry={editingEntry}
              onLog={state.logFood}
              onUpdate={updateFood}
              isPending={state.isUpdating}
            />
          )}
        </>
      ) : (
        <Accordion type="multiple" defaultValue={["weight"]}>
          <AccordionItem value="weight">
            <AccordionTrigger>{t("Weight")}</AccordionTrigger>
            <AccordionContent>
              <WeightEntryField latestWeight={state.latestWeight} onLog={state.logWeight} isPending={state.isLogging} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="food">
            <AccordionTrigger>{t("Food")}</AccordionTrigger>
            <AccordionContent>
              <FoodEntrySection onLog={state.logFood} isPending={state.isLogging} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="water">
            <AccordionTrigger>{t("Water")}</AccordionTrigger>
            <AccordionContent>
              <WaterEntryField onLog={state.logWater} isPending={state.isLogging} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};
